import { motion, AnimatePresence } from 'motion/react'
import { useSweepstakes } from '../../contexts/SweepstakesContext'
import Step1PriorityChoice from './Step1PriorityChoice'
import Step2PersonalInfo from './Step2PersonalInfo'
import Step3Contact from './Step3Contact'
import Step4WeddingDetails from './Step4WeddingDetails'
import Step5Terms from './Step5Terms'
import ThankYouMessage from './ThankYouMessage'
import { Gift } from 'lucide-react'

/**
 * SweepstakesForm
 * Main container for the multi-step sweepstakes form
 * Handles step navigation and progress display
 */
const SweepstakesForm = () => {
  const { currentStep, totalSteps, showThankYou } = useSweepstakes()

  // If submission is complete, show thank you message
  if (showThankYou) {
    return <ThankYouMessage />
  }

  // Calculate progress percentage
  const progressPercentage = (currentStep / totalSteps) * 100

  // Render the current step component
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1PriorityChoice />
      case 2:
        return <Step2PersonalInfo />
      case 3:
        return <Step3Contact />
      case 4:
        return <Step4WeddingDetails />
      case 5:
        return <Step5Terms />
      default:
        return <Step1PriorityChoice />
    }
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 pb-8">
        <div className="flex items-center justify-center mb-3">
          <Gift className="w-8 h-8 mr-2" />
          <h2 className="text-2xl font-bold">Win a Free Anniversary Photo Shoot!</h2>
        </div>
        <p className="text-center text-sm text-white/90 max-w-md mx-auto">
          Share a few quick details below for a chance to win a free anniversary session with our team. A little gift from us to you.
        </p>

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="font-medium">Step {currentStep} of {totalSteps}</span>
            <span className="font-medium">{Math.round(progressPercentage)}%</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden">
            <motion.div
              className="bg-white h-full rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progressPercentage}%` }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            />
          </div>
        </div>
      </div>

      {/* Step Content with Animation */}
      <div className="p-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

export default SweepstakesForm
